export const PLACE_IN_BOUNDS_GAP_PX = 4
export const PLACE_IN_BOUNDS_PAD_PX = 8

/** Insets inside the scene zone. Toolbar and timeline are outside that zone. */
export const SCENE_STAGE_EDGE_PADS: PlaceEdgePads = {
  top: PLACE_IN_BOUNDS_PAD_PX,
  right: PLACE_IN_BOUNDS_PAD_PX,
  bottom: PLACE_IN_BOUNDS_PAD_PX,
  left: PLACE_IN_BOUNDS_PAD_PX,
}

export type PlaceEdgePads = {
  top: number
  right: number
  bottom: number
  left: number
}

export type PlaceSide = "up" | "down" | "left" | "right"
export type PlaceAlign = "start" | "center"

export type PlaceRect = {
  x: number
  y: number
  width: number
  height: number
}

export type PlaceSize = {
  width: number
  height: number
}

export type PlaceBounds = {
  width: number
  height: number
}

export type PlaceInBoundsInput = {
  anchor: PlaceRect
  size: PlaceSize
  bounds: PlaceBounds
  prefer: PlaceSide
  /** Cross-axis alignment. Default start (panel edge flush with the anchor). */
  align?: PlaceAlign
  gap?: number
  /** One number applies to every side. */
  pad?: number | PlaceEdgePads
}

export type PlaceInBoundsResult = {
  left: number
  top: number
  side: PlaceSide
}

function opposite(side: PlaceSide): PlaceSide {
  switch (side) {
    case "up":
      return "down"
    case "down":
      return "up"
    case "left":
      return "right"
    case "right":
      return "left"
  }
}

function isHorizontal(side: PlaceSide): boolean {
  return side === "left" || side === "right"
}

export function resolvePlacePads(pad: number | PlaceEdgePads | undefined): PlaceEdgePads {
  if (pad == null) {
    return {
      top: PLACE_IN_BOUNDS_PAD_PX,
      right: PLACE_IN_BOUNDS_PAD_PX,
      bottom: PLACE_IN_BOUNDS_PAD_PX,
      left: PLACE_IN_BOUNDS_PAD_PX,
    }
  }
  if (typeof pad === "number") {
    return { top: pad, right: pad, bottom: pad, left: pad }
  }
  return pad
}

function axisInsets(side: PlaceSide, pads: PlaceEdgePads): { min: number; max: number } {
  if (isHorizontal(side)) return { min: pads.left, max: pads.right }
  return { min: pads.top, max: pads.bottom }
}

function clampToEdges(value: number, minInset: number, maxInset: number, boundsSpan: number, sizeSpan: number): number {
  const max = boundsSpan - sizeSpan - maxInset
  if (max < minInset) return minInset
  return Math.min(Math.max(value, minInset), max)
}

function primaryPos(side: PlaceSide, anchor: PlaceRect, size: PlaceSize, gap: number): number {
  switch (side) {
    case "right":
      return anchor.x + anchor.width + gap
    case "left":
      return anchor.x - gap - size.width
    case "down":
      return anchor.y + anchor.height + gap
    case "up":
      return anchor.y - gap - size.height
  }
}

function fits(
  side: PlaceSide,
  anchor: PlaceRect,
  size: PlaceSize,
  bounds: PlaceBounds,
  gap: number,
  pads: PlaceEdgePads,
): boolean {
  const span = isHorizontal(side) ? bounds.width : bounds.height
  const box = isHorizontal(side) ? size.width : size.height
  const { min, max: maxInset } = axisInsets(side, pads)
  const limit = span - box - maxInset
  if (limit < min) return false
  const pos = primaryPos(side, anchor, size, gap)
  return pos >= min && pos <= limit
}

function freeSpace(side: PlaceSide, anchor: PlaceRect, bounds: PlaceBounds, gap: number, pads: PlaceEdgePads): number {
  switch (side) {
    case "right":
      return bounds.width - pads.right - (anchor.x + anchor.width + gap)
    case "left":
      return anchor.x - gap - pads.left
    case "down":
      return bounds.height - pads.bottom - (anchor.y + anchor.height + gap)
    case "up":
      return anchor.y - gap - pads.top
  }
}

/**
 * Place a panel inside the stage rectangle.
 * Preferred side flips when it does not fit. The cross axis then shifts so the
 * panel stays inside the edge pads. A panel larger than the stage on the primary
 * axis keeps the preferred side and pins to the minimum inset.
 */
export function placeInBounds(input: PlaceInBoundsInput): PlaceInBoundsResult {
  const gap = input.gap ?? PLACE_IN_BOUNDS_GAP_PX
  const pads = resolvePlacePads(input.pad)
  const align = input.align ?? "start"
  const { anchor, size, bounds, prefer } = input
  const primaryInsets = axisInsets(prefer, pads)

  const primarySpan = isHorizontal(prefer) ? bounds.width : bounds.height
  const primarySize = isHorizontal(prefer) ? size.width : size.height
  const overflowsAxis = primarySize + primaryInsets.min + primaryInsets.max > primarySpan

  let side = prefer
  if (!overflowsAxis && !fits(prefer, anchor, size, bounds, gap, pads)) {
    const other = opposite(prefer)
    const otherFits = fits(other, anchor, size, bounds, gap, pads)
    const otherRoomier = freeSpace(other, anchor, bounds, gap, pads) > freeSpace(prefer, anchor, bounds, gap, pads)
    if (otherFits || otherRoomier) side = other
  }

  const placedInsets = axisInsets(side, pads)
  const primary = clampToEdges(
    primaryPos(side, anchor, size, gap),
    placedInsets.min,
    placedInsets.max,
    isHorizontal(side) ? bounds.width : bounds.height,
    isHorizontal(side) ? size.width : size.height,
  )

  if (isHorizontal(side)) {
    const rawTop = align === "center" ? anchor.y + anchor.height / 2 - size.height / 2 : anchor.y
    return {
      left: primary,
      top: clampToEdges(rawTop, pads.top, pads.bottom, bounds.height, size.height),
      side,
    }
  }

  const rawLeft = align === "center" ? anchor.x + anchor.width / 2 - size.width / 2 : anchor.x
  return {
    left: clampToEdges(rawLeft, pads.left, pads.right, bounds.width, size.width),
    top: primary,
    side,
  }
}
